/**
 * DebugLogger SDK
 * Global debugging utility for the application
 */
class DebugLoggerSDK {
  constructor() {
    this.isEnabled = false
    this.logs = []
    this.maxLogs = 250
  }

  /**
   * Enable the debug logger
   */
  on() {
    this.isEnabled = true
    this.dispatchEvent('debug-toggle')
    console.log('🔍 Debug Logger enabled')
  }

  /**
   * Disable the debug logger
   */
  off() {
    this.isEnabled = false
    this.dispatchEvent('debug-toggle')
    console.log('🔍 Debug Logger disabled')
  }

  /**
   * Toggle the debug logger state
   */
  toggle() {
    if (this.isEnabled) {
      this.off()
    } else {
      this.on()
    }
  }

  /**
   * Log an info message
   * @param {string} message - The log message
   * @param {object} data - Additional data to log
   */
  info(message, data = {}) {
    this.log('info', message, data)
  }

  /**
   * Log a success message
   * @param {string} message - The log message
   * @param {object} data - Additional data to log
   */
  success(message, data = {}) {
    this.log('success', message, data)
  }

  /**
   * Log a warning message
   * @param {string} message - The log message
   * @param {object} data - Additional data to log
   */
  warn(message, data = {}) {
    this.log('warn', message, data)
  }

  /**
   * Log an error message
   * @param {string} message - The log message
   * @param {object} data - Additional data to log
   */
  error(message, data = {}) {
    this.log('error', message, data)
  }

  /**
   * Internal log method
   * @param {string} level - Log level (info, success, warn, error)
   * @param {string} message - The log message
   * @param {object} data - Additional data to log
   */
  log(level, message, data = {}) {
    if (!this.isEnabled) return

    const logEntry = {
      level,
      message,
      data,
      timestamp: new Date(),
    }

    // Add to internal logs array
    this.logs.push(logEntry)

    // Keep only last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs)
    }

    // Dispatch event to the debug logger component
    this.dispatchEvent('debug-log', logEntry)

    // Also log to console for development
    const consoleMethod =
      level === 'error' ? 'error' : level === 'warn' ? 'warn' : level === 'success' ? 'log' : 'log'

    console[consoleMethod](`[DebugLogger ${level.toUpperCase()}] ${message}`, data)
  }

  /**
   * Clear all logs
   */
  clear() {
    this.logs = []
    this.dispatchEvent('debug-clear')
    console.log('🔍 Debug Logger cleared')
  }

  /**
   * Get all logs
   * @returns {Array} Array of log entries
   */
  getLogs() {
    return [...this.logs]
  }

  /**
   * Get logs by level
   * @param {string} level - Log level to filter by
   * @returns {Array} Array of filtered log entries
   */
  getLogsByLevel(level) {
    return this.logs.filter((log) => log.level === level)
  }

  /**
   * Get the current state
   * @returns {object} Current debug logger state
   */
  getState() {
    return {
      isEnabled: this.isEnabled,
      logCount: this.logs.length,
      maxLogs: this.maxLogs,
    }
  }

  /**
   * Set maximum number of logs to keep
   * @param {number} maxLogs - Maximum number of logs
   */
  setMaxLogs(maxLogs) {
    this.maxLogs = maxLogs
    if (this.logs.length > maxLogs) {
      this.logs = this.logs.slice(-maxLogs)
    }
  }

  /**
   * Dispatch a custom event
   * @param {string} eventName - Name of the event
   * @param {object} detail - Event detail data
   */
  dispatchEvent(eventName, detail = null) {
    const event = new CustomEvent(eventName, { detail })
    window.dispatchEvent(event)
  }

  /**
   * Export logs as JSON
   * @returns {string} JSON string of all logs
   */
  exportLogs() {
    return JSON.stringify(this.logs, null, 2)
  }

  /**
   * Copy logs to clipboard
   * @returns {Promise<boolean>} Success status
   */
  async copyLogs() {
    try {
      const logsJson = this.exportLogs()
      await navigator.clipboard.writeText(logsJson)
      console.log('📋 Debug logs copied to clipboard')
      return true
    } catch (error) {
      console.error('Failed to copy logs to clipboard:', error)
      return false
    }
  }

  /**
   * Download logs as a file
   */
  downloadLogs() {
    const logsJson = this.exportLogs()
    const blob = new Blob([logsJson], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `debug-logs-${new Date().toISOString().slice(0, 19)}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }
}

// Create global instance
const debugLogger = new DebugLoggerSDK()

// Make it available globally
window.debugger = debugLogger

// Also expose as debugLogger for clarity
window.debugLogger = debugLogger

export default debugLogger
